import Interieur from "./Interieur";
import Tuer from "./Tuer";
import Einlegeboden from "./Einlegeboden";
import { v3 } from "oc-threejs/short3";
import Brett from "./Brett";
import Measurer from "../interaction/Measurer";
import Bohrlochleiste from "./Bohrlochleiste";
import Maths from "oc-commons/Maths";
import Helper from "../model/Helper";
import type { HasLocalCoordinates } from "./HasLocalCoordinates";
import Schrank from "./Schrank";
import Commons from "oc-commons/Commons";
import Fach from "./Fach";
import SchwebetuerFluegel from "./SchwebetuerFluegel";
import Schubladenbox from "./Schubladenbox";
import Konstruktionsboden from "./Konstruktionsboden";

export default class Element implements HasLocalCoordinates {
    // DO NOT CHANGE
    className = "Element";

    // persistent (UserLayer) ------------------------------------------

    size = v3();
    locked = {
        x: false,
        y: false,
        z: false,
    };
    interieurs: Interieur[] = [];

    // transient (CompiledLayer) ---------------------------------------

    parent: Object;
    // die Box im lokalen System des Parents
    box = new THREE.Box3();
    // die Box im lokalen System des Schranks
    schrankBox = new THREE.Box3();

    selected = false;
    sloped = false;
    frontSloped = false;

    // nur im Falle von frontSloped=true jeweils kleiner box.max.y
    slopeTop = 0;
    slopeTopLeft = 0;
    slopeTopRight = 0;

    schrankSlopeTop = 0;
    schrankSlopeTopLeft = 0;
    schrankSlopeTopRight = 0;

    object3d: ?THREE.Object3D;
    wallLeft: Object;
    wallRight: Object;
    deckel: Brett[] = [];
    rueckwand: Brett[] = [];
    konstruktionsboeden: Brett[] = [];
    topSchublade = null;

    // die Faecher sind im lokalen System des Schranks
    faecher: THREE.Box3[] = [];

    bohrlochleisten: Bohrlochleiste[] = [];
    verdecktAnderenSchrank: ?{ schrank: Object, verdeckungsInterval: number[], elemente: Element[] };
    unverdeckt = {
        linksVerdeckt: false,
        rechtsVerdeckt: false,
        size: v3(),
        box: new THREE.Box3(),
    };
    verdecktVonElementen: Element[] = [];

    constructor(parent) {
        this.parent = parent;
        this.deckel = [];
        this.boden = [];
        this.rueckwand = [];
    }

    get schrank() {
        return this.parent.schrank;
    }

    get arrangement() {
        return this.schrank.arrangement;
    }

    get product() {
        const productName = this.isSchraeg()
            ? (this.parent.angleSide === "left" && this.isFirst()) || (this.parent.angleSide === "right" && this.isLast())
                ? "ElementSchraegErstes"
                : "ElementSchraeg"
            : "Element";
        return APP.findProduct(productName);
    }

    toJSON() {
        const konstruktionsboeden = this.konstruktionsboeden.map((boden) => {
            const json = boden.toJSON();
            json.interieur = {};
            this.interieurs.forEach((interieur) => {
                if (interieur.boden.top == boden) {
                    json.interieur.bottom = interieur.artikelnummer;
                }
                if (interieur.boden.bottom == boden) {
                    json.interieur.top = interieur.artikelnummer;
                }
            });
            return json;
        });

        return {
            className: this.className,
            size: this.size,
            locked: this.locked,
            interieurs: this.interieurs.map((i) => i.toJSON()),
            konstruktionsboeden: konstruktionsboeden,
            isEckElement: this.isEckElement,
            box: this.box,
            schrankBox: this.schrankBox
        };
    }

    fromJSON(json) {
        this.size.fromJSON(json.size);
        this.locked = _.cloneDeep(json.locked);
        this.interieurs = Commons.mapreuse(
            json.interieurs.filter((i) => !i.disableInteraction),
            this.interieurs,
            (x) => APP.model.createInterieur(x.type, this),
            (d, s) => d.fromJSON(s)
        );
        return this;
    }

    copy(element, createInterceptor) {
        this.size.copy(element.size);
        this.locked.x = element.locked.x;
        this.locked.y = element.locked.y;
        this.locked.z = element.locked.z;
        this.interieurs = Commons.mapreuse(
            element.interieurs,
            this.interieurs,
            (s) => createInterceptor((d_, s_) => APP.model.createInterieur(s_.type, this), null, s, this),
            (d, s) => createInterceptor((d_, s_) => d_.copy(s_, createInterceptor), d, s, this)
        );
        return this;
    }

    compileBox(elementXMin) {
        this.box.copy(this.parent.elementsBox);
        if (this.locked.y) {
            this.box.max.y = this.box.min.y + this.size.y;
        }
        
        this.box.min.x = elementXMin;
        this.box.max.x = this.box.min.x + this.size.x;
        this._compileSloping();
        this.setSize(this.box.getSize());
        this.schrankBox.min = this.localToSchrank(this.box.min);
        this.schrankBox.max = this.localToSchrank(this.box.max);
        // if(this.object3d) {
        //     this.object3d.setSize(this.box.getSize());
        // }
        this.unverdeckt.box.copy(this.box);
    }

    isFirst() {
        return _.first(this.parent.elements) === this;
    }

    isLast() {
        return _.last(this.parent.elements) === this;
    }

    compileParts() {
        APP.model.lazyCreateObject3d(this, () => {
            const object = APP.model.createObject3D(this);
            this.parent.child3dContainer.add(object);
            return object;
        });
        this._compileInterieursSizeAndBox();
        this._compileKonstruktionsboeden();
        this._compileInterieurBodenRelations();
        this._compileInterieursInternals();
        this._compileFaecher();
        this._compileBohrlochleisten();
        if (this.parent.schrankboden) {
            this._validate();
        } else {
            console.log("no validate", this);
        }
    }

    compileFrontCounts() {
        if (this.hasFronts) {
            if (this.wallLeft) this.wallLeft.frontCount++;
            if (this.wallRight) this.wallRight.frontCount++;
        }
        this.interieurs
            .filter((i) => i.hasFronts)
            .forEach((i) => {
                if (i.boden.top) {
                    i.boden.top.frontCount++;
                }
                if (i.boden.bottom) {
                    i.boden.bottom.frontCount++;
                }
            });
    }

    compileFronts() {
        this.interieurs.forEach((i) => i.compileFront());
    }

    _compileSloping() {
        if (this.parent.angleSide === "back") {
            this.sloped = true;
            this.frontSloped = false;
        } else {
            const deckelStaerke = this.schrank.withDeckel?this.schrank.options.getFloat("deckel_staerke"):0;
            this.slopeTopLeft = this.parent.slopings.yOfX(this.box.min.x, deckelStaerke);
            this.slopeTopRight = this.parent.slopings.yOfX(this.box.max.x, deckelStaerke);
            this.slopeTop = Math.min(this.slopeTopLeft, this.slopeTopRight);
            this.sloped = this.slopeTop + 0.01 < this.box.max.y;
            this.frontSloped = this.sloped;
        }
        if (!this.frontSloped) {
            this.slopeTopRight = this.slopeTopLeft = this.slopeTop = this.box.max.y;
        }
        this.schrankSlopeTopLeft = this.parent.localToSchrank(v3(0, this.slopeTopLeft, 0)).y;
        this.schrankSlopeTopRight = this.parent.localToSchrank(v3(0, this.slopeTopRight, 0)).y;
        this.schrankSlopeTop = this.parent.localToSchrank(v3(0, this.slopeTop, 0)).y;
    }

    _compileInterieursSizeAndBox() {
        this.interieurs.forEach((i) => i.compileSizeAndBox());
    }

    _compileInterieursInternals() {
        this.interieurs.forEach((i) => i.compileInternals());
    }

    _compileKonstruktionsboeden() {
        const lazyBoeden = APP.model.lazyPool(this, "konstruktionsboeden", () => {
            const brett = new Brett(this, "Konstruktionsboden", "xz");
            brett.isInner = true;
            return brett;
        });
        const h = this.schrank.options.getFloat("brett_staerke");
        const vorsprung = this.schrank.options.getFloat("aussenwand_vorsprung");

        const schrankKsBoedenY = [];
        this.schrank.elements.forEach((el, i) => el.interieurs.forEach(interieur => {
            if(interieur instanceof Konstruktionsboden) {
                // schrankKsBoeden.push({
                //     ksBox: interieur.box.clone(),
                //     interieurIndex: ksIndex,
                //     elementIndex: i
                // });
                schrankKsBoedenY.push(interieur.y);
            }
        }));
        const ys = [];
        this.interieurs.forEach((interieur) => {
            if (interieur.needsBoden.bottom) {
                ys.push(interieur.box.min.y - h);
            }
            if (interieur.needsBoden.top) {
                ys.push(interieur.box.max.y);
            }
        });
        const bodenAllowedBox = this.box.clone().expandByVector(v3(10, 0, 10)); //.expandByVector(v3(0,-h,0));
        if (!this.schrank.withDeckel){
            bodenAllowedBox.max.y-=h;
        }
        // this.parent.child3dContainer.add(Debugs.box(bodenAllowedBox.clone().expandByVector(v3(0,0,5)),0xff0000,true));
        // ys.filter(y => !schrankKsBoedenY.some(k => Commons.isEqualEpsilon(k,y, 0.1)))
        _.uniqWith(ys.filter(y => !schrankKsBoedenY.some(k => Commons.isEqualEpsilon(k,y, 0.1))), (a, b) => Math.abs(a - b) <= h)
            .map((y) => {
                const box = this.box.clone();
                box.min.y = y;
                box.max.y = y + h;
                box.min.z = 0;//this.getInterieurMinZ(box.min.y, box.max.y);
                box.max.z -= vorsprung;
                return box;
            })
            .filter((box) => bodenAllowedBox.containsBox(box))
            .forEach((box) => {
                const boden = lazyBoeden.next();
                boden.box.copy(box);
                boden.boxToVertices();
            });
        lazyBoeden.cleanup();

        this.konstruktionsboeden.forEach((boden) => boden.compile());
    }

    _compileFaecher() {
        this.faecher = [];
        const boeden = this.konstruktionsboeden.map((b) => b.schrankBox);

        const topEbene = this.schrankBox.clone();
        topEbene.min.y = topEbene.max.y;
        boeden.push(topEbene);

        // if (this.isSchraeg()) {
        //     const slopeEbene = this.box.clone();
        //     slopeEbene.min.y = slopeEbene.max.y = this.slopeTop;
        //     boeden.push(slopeEbene);
        // }

        boeden.sort((a, b) => a.min.y - b.min.y);

        let lastBodenY = this.parent.schrankboden ? this.parent.schrankboden.schrankBox.max.y : this.schrankBox.min.y;
        const brettStaerke = this.schrank.options.getFloat("brett_staerke");

        boeden.forEach((boden) => {
            const fach = this.schrankBox.clone();
            fach.min.y = lastBodenY;
            fach.max.y = boden.min.y;
            // im SlopeTop kann es bei Konstruktionsböden sonst zu Artefakt-Fächern kommen
            if (fach.max.y - fach.min.y > brettStaerke - 0.0001) {
                this.faecher.push(fach);
            }
            lastBodenY = boden.max.y;
        });
        this.elementFaecher = Commons.mapreuse(
            this.faecher,
            this.elementFaecher,
            (e) => {
                return new Fach(this);
            },
            (fach, data) => {
                fach.box = data;
                return fach;
            }
        );
        this.elementFaecher.forEach((f) => f.compile());
    }

    _compileInterieurBodenRelations() {
        let boeden: Brett[] = (this.parent.schrankboden ? [this.parent.schrankboden] : [])
            .concat(this.konstruktionsboeden)
            .concat(this.deckel.filter((b) => b.isPlanar("y")));

        const h = this.schrank.options.getFloat("brett_staerke");
        boeden.forEach((b) => (b.interieurCount = 0));
        this.interieurs.forEach((i) => {
            i.boden.top = null;
            i.boden.bottom = null;
            if (i.needsBoden.top || i.needsBoden.bottom) {
                const intersectingKS = [];
                boeden.forEach(bo => intersectingKS.push(this.konstruktionsboeden.some(ks => ks.box.intersectsBox(bo.box))));
                const alreadyKS = intersectingKS.some(i => i == true);

                boeden.forEach((b) => {
                    const bodenIsKS = this.konstruktionsboeden.find(ks => ks.box.intersectsBox(b.box));
                    
                    if (i.needsBoden.top) {
                        if (Math.abs(i.schrankBox.max.y - b.schrankBox.min.y) < h) {
                            // if(!!bodenIsKS) {
                            //     i.boden.top = bodenIsKS;
                            // } else {
                            // }
                            const bodenIsKS = this.konstruktionsboeden.find(ks => ks.box.intersectsBox(b.box))
                            i.boden.top = !!bodenIsKS ? bodenIsKS : b;
                            b.interieurCount++;
                        }
                    }
                    if (i.needsBoden.bottom) {
                        if (Math.abs(i.schrankBox.min.y - b.schrankBox.max.y) < h) {
                            i.boden.bottom = b;
                            b.interieurCount++;
                        }
                    }
                });
            }
        });
    }

    _compileBohrlochleisten() {
        const leistenTypes = new Set();
        this.interieurs.forEach((interieur) => {
            if (interieur.bohrlochleiste) {
                leistenTypes.add(interieur.bohrlochleiste);
            }
        });
        const lazyBohrlochleisten = APP.model.lazyPool(this, "bohrlochleisten", () => new Bohrlochleiste(this));
        leistenTypes.map((type) => {
            
            const cfgArray = Bohrlochleiste.lochleistenCfg(this.schrank)[type];
            cfgArray.forEach((cfg) => {
                const bohrlochleiste = lazyBohrlochleisten.next();
                bohrlochleiste.cfg = cfg;
                bohrlochleiste.compile();
            });
        });
        lazyBohrlochleisten.cleanup();
    }

    _compileFixedTopSchublade(hasTopSchubladen = false) {
        if(this.fixedTopSchublade) {
            this.fixedTopSchublade.element = null;
            this.interieurs = this.interieurs.filter((interieur) => interieur !== this.fixedTopSchublade);
            this.fixedTopSchublade = undefined;
        }
        if(hasTopSchubladen && this.wallLeft && this.wallRight && !this.sloped) {
            const newSchubladenbox = APP.model.lazyPool(this, "schubladenbox", () => new Schubladenbox("Schubladenbox", this)).next();
            if(newSchubladenbox && !this.fixedTopSchublade) {
                this.fixedTopSchublade = this._createFixedTopSchublade(newSchubladenbox);
                this.interieurs.push(this.fixedTopSchublade);
            }
            this.fixedTopSchublade.position.y = this.slopeTop - this.fixedTopSchublade.size.y / 2;
        }
    }

    _createFixedTopSchublade(schubladenbox) {
        const product = APP.findProductsByType("Schubladenbox")[0];
        schubladenbox.product = product;
        schubladenbox.isDropped = true;
        schubladenbox.disableInteraction = true;
        schubladenbox.fullHeightDrag = true;
        schubladenbox.element = this;
        return schubladenbox;
    }

    setSize(boxSize) {
        ["x", "y", "z"].forEach((a) => {
            if (!this.locked[a]) {
                this.size[a] = boxSize[a];
            }
        });
    }

    clearLocked(direction = null) {
        if (direction === null) {
            this.locked.x = false;
            this.locked.y = false;
            this.locked.z = false;
        } else {
            this.locked[direction] = false;
        }
    }

    getInterieurMinMaxBoxWithoutFullHeightDrag() {
        const box = new THREE.Box3();
        this.interieurs.filter((i) => !(i.fullHeightDrag || i.dragging)).forEach((i) => box.union(i.getSpaceBoxForValidation()));
        return box;
    }

    setDimension(dir: string, value: number) {
        if (dir === "y") {
            value -= this.outerOffset;
        }
        this.locked[dir] = true;
        this.size[dir] = value;
        this._checkSizes();
        if (dir === "y") {
            this.parent.setHeightToElementMaximum();
        }
    }

    _checkSizes() {
        const check = (minmax: string, value: number, borderValue: number, title: string, message: string, offset = 0) => {
            const epsilon = 0.01;
            const compareValue = value + offset;
            if ((minmax == "min" && compareValue + epsilon < borderValue) || (minmax == "max" && compareValue > borderValue + epsilon)) {
                //console.log(compareValue,minmax,borderValue);
                value = borderValue - offset;
                APP.model.maybeThrowCompileError(this, title, message, borderValue);
            }
            return value;
        };
        let maxHeightI18nKey = "maxHeightText";
        if (this.schrank.isSchwebetuer) {
            maxHeightI18nKey = "maxHeightTextAlternative";
        }
        const box = this.getInterieurMinMaxBoxWithoutFullHeightDrag();
        let wandstaerken = 0;
        if (this.wallLeft) wandstaerken += this.wallLeft.size.x;
        if (this.wallRight) wandstaerken += this.wallRight.size.x;
        if (this.getNumberOfParentElements() === 0) {
            box.min.x = this.schrank.options.getMinValue("element_breite"); // - wandstaerken;
            box.max.x = this.schrank.options.getMaxValue("element_breite"); // - wandstaerken;
        } else {
            const min = Math.max(this.schrank.options.getMinValue("trennwandbox_fach_breite") - wandstaerken);
            const max = Math.min(
                this.schrank.options.getMaxValue("trennwandbox_fach_breite") - wandstaerken,
                this.parent.size.x - this.schrank.options.getMinValue("trennwandbox_fach_breite")
            );
            box.min.x = min;
            box.max.x = max;
        }

        this.size.x = check("min", this.size.x, box.min.x, "minWidth", "minWidthText");
        this.size.x = check("max", this.size.x, box.max.x, "maxWidth", "maxWidthText");
        if (this.sloped) {
            if (this.schrank.options.getValue("dachschraege") === "right") {
                this.size.x = check("max", this.size.x, this.getDoorMaxWidth("links"), "notice", "doorCanNotAdded");
                this.size.x = check("min", this.size.x, this.getDoorMinWidth("links"), "notice", "doorCanNotAdded");
            } else if (this.schrank.options.getValue("dachschraege") === "left") {
                this.size.x = check("max", this.size.x, this.getDoorMaxWidth("rechts"), "notice", "doorCanNotAdded");
                this.size.x = check("min", this.size.x, this.getDoorMinWidth("rechts"), "notice", "doorCanNotAdded");
            }
        } else {
            this.size.x = check("max", this.size.x, this.getDoorMaxWidth("beide"), "notice", "doorCanNotAdded");
            this.size.x = check("min", this.size.x, this.getDoorMinWidth("beide"), "notice", "doorCanNotAdded");
        }

        //        outerOffset = bottomOffset = this.box.min.y - this.parent.box.min.y + deckelStaerke

        //Höhe des einzelnen Elementes konnte nicht mehr gesetzt werden, deshalb diese Abfrage...
        if (this.getNumberOfParentElements() > 0) {
            this.size.y = check(
                "min",
                this.schrankBox.max.y,
                this.getInterieurMinMaxBoxWithoutFullHeightDrag().max.y,
                "minHeight",
                "minHeightAusstattungText"
            );
        }

        if (this.getNumberOfParentElements() === 0) {
            const deckelStaerke = this.schrank.options.getFloat("deckel_staerke", 1.9);
            this.size.y = check(
                "min",
                this.size.y,
                this.schrank.options.getMinValue("schrank_hoehe"),
                "minHeight",
                "minHeightText",
                this.outerOffset
            );
            this.size.y = check(
                "max",
                this.size.y,
                this.schrank.options.getMaxValue("schrank_hoehe"),
                "maxHeight",
                maxHeightI18nKey,
                this.outerOffset
            );
        }

        // JVI: Wird nicht gemacht, weil die Elementhöhe die Schrankhöhe ändert
        // check('max',this.parent.elementsBox.getSize().y, 'maxHeightElement', 'maxHeightTextElement');
        this.size.y = check("min", this.size.y, 0, "minHeightElement", "minHeightTextElement");

        if (this.unverdeckt.linksVerdeckt || this.unverdeckt.rechtsVerdeckt) {
            const eckschrank_oeffnung = this.schrank.options.getFloat("eckschrank_oeffnung");
            const epsilon = 0.00001;
            const size = this.unverdeckt.size.x;
            if (size + epsilon < eckschrank_oeffnung) {
                APP.model.maybeThrowCompileError(
                    this,
                    "compileErrorConstruction",
                    "minimalEckschrankOeffnung",
                    this.product.Name,
                    eckschrank_oeffnung,
                    size
                );
            }
        }
    }

    getDoorMaxWidth(side) {
        let max = 10000;
        this.getDoors()
            .filter((x) => side === x.getSide())
            .forEach((door) => (max = Math.min(max, door.product.BreiteMax)));
        return max;
    }

    getDoorMinWidth(side) {
        let min = 0;
        this.getDoors()
            .filter((x) => side === x.getSide())
            .forEach((door) => (min = Math.max(min, door.product.BreiteMin)));
        return min;
    }

    getMinMax(withInterieurs = false) {
        const minMax = new THREE.Box3();
        minMax.min.x = this.schrank.options.getMinValue("element_breite");
        minMax.max.x = this.schrank.options.getMaxValue("element_breite");
        // JVI: Achtung! Das hier sind Aussenmasse!
        minMax.min.y = this.schrank.options.getMinValue("schrank_hoehe");
        minMax.max.y = this.schrank.options.getMaxValue("schrank_hoehe");
        minMax.min.z = this.schrank.options.getMinValue("schrank_tiefe");
        minMax.max.z = this.schrank.options.getMaxValue("schrank_tiefe");
        if (this.schrank.isPartOfEcksystem) {
            this.verdecktVonElementen.forEach((element) => {
                const tuerArtikelnummer = this.schrank.schenkel === "links" ? "Tür-einflügelig-rechts" : "Tür-einflügelig-links";
                const tuerProdukt = APP.findProduct(tuerArtikelnummer);
                const minSize =
                    element.schrank.size.z + this.schrank.options.getFloat("eckschrank_oeffnung") - this.schrank.options.getFloat("rueckwand_dicke");
                const maxSize = element.schrank.size.z + tuerProdukt.BreiteMax;
                minMax.min.x = Math.max(minMax.min.x, minSize);
                minMax.max.x = Math.min(minMax.max.x, maxSize);
            });
            /* if (this.verdecktAnderenSchrank) {
                const minSize = this.schrank.options.getFloat("eckschrank_verdeckendes_element_breite");
                minMax.max.x = Math.min(minMax.max.x, minSize);
            } */
        }
        //this.interieurs.forEach(i => minMax.intersect(i.getMinMax()));
        if (withInterieurs) {
            const boxClone = minMax.clone();
            this.interieurs.forEach((i) => {
                boxClone.min.x = i.product.BreiteMin;
                boxClone.max.x = i.product.BreiteMax;
                minMax.intersect(boxClone);
            });
        }
        return minMax;
    }

    isSchraeg() {
        const knickAt = this.parent.slopeX;
        return (
            (this.parent.angleSide === "left" && this.box.min.x < knickAt) ||
            (this.parent.angleSide === "right" && this.box.max.x > knickAt) ||
            this.parent.angleSide === "back"
        );
    }

    get isEckElement() {
        if (this.verdecktAnderenSchrank || this.verdecktVonElementen.length > 0) {
            return true;
        }
        return false;
    }

    getDoors() {
        return this.interieurs.filter((x) => x instanceof Tuer && x.isDropped);
    }

    get hasFronts() {
        return this.interieurs.some((i) => i.hasFronts);
    }

    hasEinlegeboden(ignore) {
        if (ignore) {
            return this.interieurs.some((i) => i instanceof Einlegeboden && i.object3d !== ignore);
        } else {
            return this.interieurs.some((i) => i instanceof Einlegeboden);
        }
    }

    getInterieurYFitAtPos(interieur) {
        const y = interieur.getSchrankPositionY();
        return new Measurer(Measurer.CENTER).getInterieurYFits(interieur, this, true).find((fit) => {
            return y < fit.maxCollapse && y > fit.minCollapse;
        });
    }

    getOuterHeight() {
        return this.deckel.reduce((c, d) => Math.max(c, d.box.max.y), 0);
    }

    addInterieur(interieur) {
        if (interieur.element) {
            interieur.removeFromElement();
        }
        interieur.element = this;
        this.interieurs.push(interieur);
        APP.model.setDirty();
    }

    getMaxHeightForMinDepth(interieur) {
        const interieurMinZ = Maths.cutBetween(this.box.min.z, this.box.max.z - interieur.product.TiefeMin, this.box.max.z);
        return this.parent.slopings.elementYofZ(interieurMinZ);
    }

    centerInterieurXZ(interieur) {
        const box = interieur.getElementBox(this);
        const elementCenter = box.getCenter();
        interieur.position.x = elementCenter.x;
        // wegen Dachschräge hinten wird die Box immer bündig nach vorne gesetzt
        interieur.position.z = box.max.z - interieur.size.z / 2;
    }

    getInterieurMinZ(y0, y1) {
        const minZ = _.max([y0, y1].map((y) => this.parent.slopings.elementZofY(y)));
        if (!Number.isFinite(minZ)) {
            throw "getInterieurMinZ is not finite";
        }
        return minZ;
    }

    get fullHeight() {
        return this.getOuterHeight();
    }

    get outerOffset() {
        if (this.box && !Maths.notFinite(this.box.min.y)) {
            let bottomOffset = this.box.min.y;
            if (!Maths.notFinite(this.parent.box.min.y)) {
                bottomOffset -= this.parent.box.min.y;
            }
            const deckelStaerke = this.schrank.options.getFloat("deckel_staerke");
            return Helper.round(bottomOffset + deckelStaerke);
        }
        return 0;
    }

    _validate() {
        const fach_hoehe = this.schrank.options.getFloat("fach_hoehe");
        const epsilon = 0.00001;
        // console.log('faecher-höhen',this.faecher.map(f=>f.getSize().y));
        this.faecher.forEach((f) => {
            if (f.getSize().y + epsilon < fach_hoehe) {
                APP.model.maybeThrowCompileError(
                    this,
                    "compileError",
                    "Fach-Mindesthöhe unterschritten",
                    this.product.Name,
                    f.getSize().y,
                    fach_hoehe
                );
            }
        });
        this._checkSizes();
    }

    iterateElements(iterator) {
        iterator(this);
        this.interieurs.forEach((i) => i.iterateElements(iterator));
    }

    iterateInterieur(iterator) {
        this.iterateElements((e) => e.interieurs.forEach((x) => x.iterate(iterator)));
    }

    getNumberOfParentElements() {
        let n = 0;
        let parent = this.parent;
        while (parent != null) {
            if (parent instanceof Element) {
                n++;
            }
            parent = parent.parent;
        }
        return n;
    }

    get child3dContainer() {
        return this.parent.child3dContainer;
    }

    schrankToLocal(v: THREE.Vector3) {
        return this.parent.schrankToLocal(v);
    }

    localToSchrank(v: THREE.Vector3) {
        return this.parent.localToSchrank(v);
    }

    schrankToLocalBox(b: THREE.Box3) {
        return new THREE.Box3(this.schrankToLocal(b.min), this.schrankToLocal(b.max));
    }

    localToSchrankBox(b: THREE.Box3) {
        return new THREE.Box3(this.localToSchrank(b.min), this.localToSchrank(b.max));
    }

    /**
     * ist false, falls dieses Objekt Teil des this.arrangement ist.
     */
    isOrphaned() {
        if (this.parent == null) {
            return true;
        }
        if (!this.parent.elements.includes(this)) {
            return true;
        }
        return this.parent.isOrphaned();
    }

    isChildOf(parent) {
        return this.parent !== null && (this.parent === parent || this.parent.isChildOf(parent));
    }

    idxInParent() {
        return this.parent == null ? null : this.parent.elements.indexOf(this);
    }

    iterate(iterator) {
        iterator(this);
        this.interieurs.forEach((x) => x.iterate(iterator));
        this.konstruktionsboeden.forEach((x) => x.iterate(iterator));
        this.elementFaecher.forEach((x) => x.iterate(iterator));
    }
}
